import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, MapPin, MessageCircle, Mail, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchStoreInfo } from '../services/api';

// Use the local logo file directly (cache-busted)
const LOGO_URL = '/logo.png?v=2';

const Footer = () => {
  const { language, t } = useLanguage();
  const [storeInfo, setStoreInfo] = useState({});

  useEffect(() => {
    fetchStoreInfo()
      .then((data) => setStoreInfo(data))
      .catch(() => {});
  }, []);

  const handleWhatsApp = () => {
    if (storeInfo.whatsapp) {
      window.open(`https://wa.me/${storeInfo.whatsapp.replace(/[^0-9]/g, '')}`, '_blank');
    }
  };

  const addresses = Array.isArray(storeInfo.addresses) && storeInfo.addresses.length > 0
    ? storeInfo.addresses
    : (storeInfo.address ? [{ id: 'legacy', label_tr: 'Mağaza', label_en: 'Store', address_tr: storeInfo.address, address_en: storeInfo.addressEn || storeInfo.address }] : []);

  return (
    <footer className="bg-[#1a1a1a] text-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <img
              src={LOGO_URL}
              alt="Altınzade Kuyumculuk"
              className="h-20 w-auto mb-6"
            />
            <p className="text-[#bcbbb4] text-sm leading-relaxed max-w-md" data-testid="footer-tagline">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-base font-medium mb-6 text-white">
              {t('footer.quickLinks')}
            </h4>
            <nav className="flex flex-col gap-3">
              <Link to="/" className="text-[#bcbbb4] text-sm hover:text-white transition-colors duration-300">{t('nav.home')}</Link>
              <Link to="/collections" className="text-[#bcbbb4] text-sm hover:text-white transition-colors duration-300">{t('nav.collections')}</Link>
              <Link to="/about" className="text-[#bcbbb4] text-sm hover:text-white transition-colors duration-300">{t('nav.about')}</Link>
              <Link to="/contact" className="text-[#bcbbb4] text-sm hover:text-white transition-colors duration-300">{t('nav.contact')}</Link>
            </nav>
          </div>

          {/* Contact (mirrors ContactPage) */}
          <div>
            <h4 className="text-base font-medium mb-6 text-white">
              {t('footer.contactInfo')}
            </h4>
            <div className="space-y-4">
              {addresses.map((addr, idx) => {
                const text = language === 'tr' ? addr.address_tr : addr.address_en;
                const label = language === 'tr' ? addr.label_tr : addr.label_en;
                if (!text) return null;
                return (
                  <div key={addr.id || idx} className="flex items-start gap-3" data-testid={`footer-address-${idx}`}>
                    <MapPin className="w-5 h-5 text-[#b8860b] flex-shrink-0 mt-0.5" />
                    <div>
                      {label && <p className="text-white text-xs tracking-wide uppercase mb-1">{label}</p>}
                      <p className="text-[#bcbbb4] text-sm leading-relaxed">{text}</p>
                    </div>
                  </div>
                );
              })}

              {storeInfo.email && (
                <a
                  href={`mailto:${storeInfo.email}`}
                  className="flex items-start gap-3 text-[#bcbbb4] text-sm hover:text-white transition-colors duration-300"
                  data-testid="footer-email-link"
                >
                  <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="break-all">{storeInfo.email}</span>
                </a>
              )}

              <button
                onClick={handleWhatsApp}
                data-testid="footer-whatsapp-btn"
                className="flex items-center gap-3 text-[#bcbbb4] text-sm hover:text-[#25d366] transition-colors duration-300"
              >
                <MessageCircle className="w-5 h-5 flex-shrink-0" />
                {storeInfo.whatsappDisplay || storeInfo.whatsapp}
              </button>

              {storeInfo.instagram && (
                <a
                  href={`https://instagram.com/${storeInfo.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#bcbbb4] text-sm hover:text-[#e1306c] transition-colors duration-300"
                >
                  <Instagram className="w-5 h-5 flex-shrink-0" />
                  @{storeInfo.instagram}
                </a>
              )}

              {(storeInfo.hours_tr || storeInfo.hours_en) && (
                <div className="flex items-start gap-3 pt-2" data-testid="footer-hours">
                  <Clock className="w-5 h-5 text-[#b8860b] flex-shrink-0 mt-0.5" />
                  <p className="text-[#bcbbb4] text-sm leading-relaxed whitespace-pre-line">
                    {language === 'tr' ? (storeInfo.hours_tr || '') : (storeInfo.hours_en || '')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#333] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#666] text-xs">
            © {new Date().getFullYear()}{' '}
            {language === 'tr' ? storeInfo.name : storeInfo.nameEn}.{' '}
            {t('footer.rights')}
          </p>
          <div className="flex items-center gap-4">
            <span className="text-[#666] text-xs">{t('footer.followUs')}:</span>
            {storeInfo.instagram && (
              <a
                href={`https://instagram.com/${storeInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#666] hover:text-white transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
